import React, { useState } from "react";
import { AppBar, Box, Button, IconButton, Link, Toolbar, Typography } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import MenuIcon from "@mui/icons-material/Menu";
import { useAuth } from "../contexts/authContext";
import layoutConstants from "../constants/layoutConstants";
import Footer from "../components/Footer";
import PlatformFooter from "../components/PlatformFooter";
import clsx from "clsx";
import { Link as RouterLink, useLocation } from "@/compat/router";
import { SettingsOutlined } from "@mui/icons-material";
import { useAppSelector } from "../redux/hooks";
import { red } from "@mui/material/colors";
import { ReactComponent as WorknestLogo } from "../images/myWorkNest_Purple_Negative_RGB.svg?react";
import HRSolutionsLogo from "../images/Business-HR-Solutions-Logo.png";
import NavigationDrawer from "../components/navigation/NavigationDrawer";
import PlatformNavigationDrawer from "../components/navigation/PlatformNavigationDrawer";
import appIds from "../constants/appIdConstants";
import UserMenu from "../components/navigation/UserMenu";
import useIsMobile from "../hooks/useIsMobile";
import QuickNavigationMenu from "../components/navigation/QuickNavigationMenu";
import KnowledgeBankBar from "../knowledgebank/KnowledgeBankBar";
import { isHRS } from "../constants/siteConfig";
import useFeatureFlag from "../hooks/useFeatureFlag";
import NestLogo from "../components/NestLogo";
import { useHistory } from "@/compat/router";

const usePlatformStyles = makeStyles((theme) => ({
    VerticalDivider: {
        width: "1px",
        height: "24px",
        backgroundColor: "#C6C6C6",
    },
    Wrapper: {
        height: "100%",
        display: "flex",
        flexDirection: "row",
        paddingTop: "80px",
        transition: "padding-left 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
    },
    WrapperShift: {
        paddingLeft: "336px",
    },
    SideNavigation: {
        height: "100%",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "16px",
        paddingBlock: "24px",
        paddingInline: "12px",
    },

    Content: {
        paddingTop: "40px",
        // `theme.breakpoints.down("sm")` is a media-query STRING (always truthy),
        // so the old `? 0 : "110px"` ternary always resolved to 0 and content
        // collided with the footer. Use a proper responsive style instead.
        paddingBottom: "110px",
        [theme.breakpoints.down("sm")]: {
            paddingBottom: 0,
        },
        width: "100%",
        margin: "0 auto",
        paddingInline: "24px",
        overflow: "auto",
    },

    IconsWrapper: {
        display: "flex",
        gap: 8,
        alignItems: "center",
    },

    AppBar: {
        backgroundColor: "white",
        borderBottomWidth: "1px",
        borderBottomStyle: "solid",
        borderBottomColor: "lightgrey",
    },

    Toolbar: {
        paddingInline: "24px",
        height: "80px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    Avatar: {
        marginLeft: "16px",
        height: "40px",
        width: "40px",
    },

    LogoutButton: {
        color: "text.secondary",
        marginTop: "auto",
    },

    IconButton: {
        color: "rgba(0, 0, 0, 0.7)",
    },

    Icon: {
        height: "20px",
        width: "20px",
        color: "#48464A"
    },

    brandText: {
        color: "#1C1B1F",
        fontSize: "20px",
        fontWeight: "bold",
        fontFamily: "Inter, Arial, sans-serif",
    },
    myWorkNestLink: {
        color: "#6050ae",
        fontSize: "14px",
        fontWeight: 600,
        fontFamily: "Inter, Arial, sans-serif",
        marginRight: "15px"
    }
}));


const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        height: "100%",
    },
    appBar: {
        transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    appBarShift: {
        [theme.breakpoints.up("sm")]: {
            marginLeft: layoutConstants.drawerWidth,
            width: `calc(100% - ${layoutConstants.drawerWidth}px)`,
        },
        transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    menuButton: {
        marginRight: theme.spacing(2),
    },
    hide: {
        display: "none",
    },
    drawerHeader: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0, 1),
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
        justifyContent: "flex-end",
        backgroundColor: theme.palette.primary.main,
    },
    main: {
        flexGrow: 1,
        transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: "100%",
        display: "flex",
        flexDirection: "column",
    },
    drawerMargin: {
        [theme.breakpoints.up("sm")]: {
            marginLeft: -layoutConstants.drawerWidth,
        },
    },
    mainShift: {
        transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
    },
    content: {
        flex: "1",
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(layoutConstants.contentPaddingMultiplier),
        borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
    },
    toolbar: theme.mixins.toolbar,
    userName: {
        fontWeight: 500,
    },
    appTitle: {
        flexGrow: 1,
        display: "flex",
        alignItems: "center",
    },
    siteSelect: {
        color: "inherit",
        "& .MuiAutocomplete-inputRoot, .MuiFormLabel-root, .MuiAutocomplete-clearIndicator, .MuiAutocomplete-popupIndicator":
        {
            color: "inherit",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "inherit",
            opacity: 0.5,
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            opacity: 1,
            borderColor: "inherit",
        },
    },
    appTitleLink: {
        display: "flex",
        alignItems: "center",
        "&:hover": {
            textDecoration: "none",
        },
    },
    userMenuContainer: {
        display: "flex",
        alignItems: "center",
        marginLeft: theme.spacing(2),
    },
    contactSupportContainer: {
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(2, 0),
        [theme.breakpoints.up("sm")]: {
            flexDirection: "row",
            marginRight: theme.spacing(2),
            padding: 0,
        },
    },
    contactSupportText: {
        fontSize: 16,
        fontWeight: theme.typography.fontWeightBold,
        [theme.breakpoints.up("sm")]: {
            flexDirection: "row",
            marginRight: theme.spacing(1),
        },
    },
    contactSupportNumber: {
        fontSize: 16,
        fontWeight: theme.typography.fontWeightLight,
        color: "#fff",
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    impersonatingBanner: {
        backgroundColor: red[500],
        color: "white",
        position: "fixed",
        zIndex: 9999,
        top: 0,
        left: 0,
        right: 0,
        height: layoutConstants.impersonationBannerHeight,
        display: "flex",
        justifyContent: "space-between",
        padding: `0 ${theme.spacing(2)}px`,
    },
    impersonatingText: {
        textAlign: "center",
    },
    impersonatingEndSession: {
        color: "white",
    },
    termsShift: {
        marginTop: layoutConstants.termsBannerHeight,
        background: "white",
    },
    impersonationShift: {
        marginTop: layoutConstants.impersonationBannerHeight,
    },
    logo: {
        width: "100%",
        maxWidth: "170px",
        maxHeight: "64px"
    },
    secondaryToolbar: {
        background: 'white',
        color: 'black'
    }
}));

const AuthLayout = ({ children }) => {
    const classes = useStyles();
    const platformClasses = usePlatformStyles();
    const isMobile = useIsMobile();
    const { activeAppId } = useAppSelector((state) => state.account);
    const { impersonatingUser, endImpersonationSession } = useAuth();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const location = useLocation();
    const { data: isPlatformEnabled } = useFeatureFlag("Platform");
    const history = useHistory();

    const layoutInfo = (() => {
        const pathStartsWith = (prefix) => location.pathname.startsWith(prefix);

        switch (true) {
            case pathStartsWith("/knowledgehub/healthandsafety"):
                return {
                    title: "ResourceNest",
                    url: process.env.REACT_APP_RESOURCE_NEST_URL
                }
            case pathStartsWith("/resourcenest/premier"):
                return {
                    title: "ResourceNest",
                    url: "/resourcenest/premier"
                }
            case pathStartsWith("/resourcenest/partners"):
                return {
                    title: "ResourceNest",
                    url: "/resourcenest/partners"
                }
            case location.pathname === "/":
                return {
                    title: "myWorkNest",
                    url: process.env.REACT_APP_MYWORKNEST_BASE_URL
                }
            case pathStartsWith("/safetynest"):
                return {
                    title: "SafetyNest",
                    url: process.env.REACT_APP_SAFETYNEST_BASE_URL
                }
            case pathStartsWith("/documents"):
                return {
                    title: "myWorkNest",
                    url: null
                }
            default:
                return {
                    title: "myWorkNest",
                    url: process.env.REACT_APP_PLATFORM_URL
                }
        }
    })();

    const showSideMenu =
        activeAppId === appIds.safetynest ||
        activeAppId === appIds.casenest ||
        isMobile;

    const handleDrawerToggle = () => {
        setDrawerOpen((current) => !current);
    };

    const handleEndImpersonationSession = async () => {
        try {
            await endImpersonationSession();
        } catch (e) {
            console.error(e);
        }
    };

    const handleSettingsClick = () => {
        history.push("/administration");
    };

    return (
        <>
            {impersonatingUser && (
                <Box className={classes.impersonatingBanner}>
                    <Typography className={classes.impersonatingText} variant="body2">
                        Impersonating{" "}
                        {`${impersonatingUser.name} (${impersonatingUser.email})`}
                    </Typography>
                    <Button
                        onClick={handleEndImpersonationSession}
                        variant="text"
                        className={classes.impersonatingEndSession}
                    >
                        End Session
                    </Button>
                </Box>
            )}
            {isHRS === false && isPlatformEnabled ? (
                <>
                    <AppBar
                        elevation={0}
                        className={platformClasses.AppBar}
                        position="fixed"
                    >
                        <Toolbar className={platformClasses.Toolbar}>
                            <div className={platformClasses.IconsWrapper} >
                                {showSideMenu && (
                                    <IconButton
                                        edge="start"
                                        className={platformClasses.IconButton}
                                        aria-label="menu"
                                        onClick={handleDrawerToggle}
                                    >
                                        <MenuIcon />
                                    </IconButton>
                                )}
                                {layoutInfo.url != null ?
                                    (
                                        <a style={{
                                            textDecoration: "none",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px"
                                        }}
                                            href={layoutInfo.url}>
                                            <NestLogo />
                                            <span className={platformClasses.brandText}>{layoutInfo.title}</span>
                                        </a>
                                    ) : (
                                        <span style={{
                                            textDecoration: "none",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px"
                                        }}
                                        >
                                            <NestLogo />
                                            <span className={platformClasses.brandText}>{layoutInfo.title}</span>
                                        </span>
                                    )}
                            </div>
                            <div className={platformClasses.IconsWrapper}>
                                {location.pathname !== "/" && (
                                    <>
                                        {impersonatingUser ? (
                                            <Link component={RouterLink} to="/" className={platformClasses.myWorkNestLink}>
                                                myWorkNest
                                            </Link>
                                        ) : (
                                            <a
                                                style={{
                                                    textDecoration: "none",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    padding: "12px",
                                                }}
                                                href={process.env.REACT_APP_PLATFORM_URL}
                                            >
                                                <span className={platformClasses.myWorkNestLink}>myWorkNest</span>
                                            </a>
                                        )}

                                        <div className={platformClasses.VerticalDivider} />
                                    </>
                                )}
                                <IconButton className={platformClasses.IconButton}>
                                    <SettingsOutlined className={platformClasses.Icon} onClick={handleSettingsClick} />
                                </IconButton>
                                {/* UserMenu renders its own IconButton; don't wrap it
                                    in another one (nested <button> is invalid HTML). */}
                                <UserMenu />
                            </div>
                        </Toolbar>
                    </AppBar>
                    {showSideMenu && (
                        <PlatformNavigationDrawer
                            isOpen={drawerOpen}
                            onToggleOpen={handleDrawerToggle}
                        />
                    )}
                    <div className={clsx(platformClasses.Wrapper, {
                        [platformClasses.WrapperShift]: drawerOpen && !isMobile && showSideMenu,
                    })}>
                        <div className={platformClasses.Content}>
                            {children}
                            <PlatformFooter isImpersonatingUser={impersonatingUser !== null && impersonatingUser !== undefined} />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className={classes.root}>
                        <AppBar
                            position="fixed"
                            className={clsx(classes.appBar, {
                                [classes.appBarShift]: showSideMenu && !isMobile && drawerOpen,
                                [classes.impersonationShift]: !!impersonatingUser,
                            })}
                        >
                            <Toolbar>
                                {showSideMenu && (
                                    <IconButton
                                        edge="start"
                                        className={clsx(classes.menuButton, drawerOpen && classes.hide)}
                                        color="inherit"
                                        aria-label="menu"
                                        onClick={handleDrawerToggle}
                                    >
                                        <MenuIcon />
                                    </IconButton>
                                )}
                                <div className={classes.appTitle}>
                                    {impersonatingUser || !isPlatformEnabled || isHRS ? (
                                        <Link
                                            component={RouterLink}
                                            to="/"
                                            className={classes.appTitleLink}
                                        >
                                            {isHRS ? <img className={classes.logo} src={HRSolutionsLogo} alt="Business HR Logo" /> : <WorknestLogo className={classes.logo} />}
                                        </Link>
                                    ) : (
                                        <a
                                            href={process.env.REACT_APP_PLATFORM_URL}
                                            className={classes.appTitleLink}
                                        >
                                        </a>
                                    )}
                                </div>
                                <div className={classes.userMenuContainer}>
                                    <QuickNavigationMenu />
                                    <UserMenu />
                                </div>
                            </Toolbar>
                            {location.pathname.startsWith("/knowledge") && !location.pathname.startsWith("/knowledgehub") && <Toolbar className={classes.secondaryToolbar}><KnowledgeBankBar /></Toolbar>}
                        </AppBar>
                        {showSideMenu && (
                            <NavigationDrawer
                                isOpen={drawerOpen}
                                onToggleOpen={handleDrawerToggle}
                            />
                        )}
                        <main
                            className={clsx(classes.main, {
                                [classes.drawerMargin]: showSideMenu && !isMobile,
                                [classes.mainShift]: drawerOpen,
                                [classes.impersonationShift]: !!impersonatingUser,
                            })}
                        >
                            <div className={classes.drawerHeader} />
                            {location.pathname.startsWith("/knowledge") && <div className={classes.drawerHeader} />}
                            <div className={classes.content}>
                                {children}
                            </div>
                            <Footer />
                        </main>
                    </div>
                </>
            )
            }
        </>
    );
};

export default AuthLayout;
